/**
 * The `agree` i18next formatter — grammatical agreement with a tenant-customizable term.
 *
 * The 11 curated business terms are renameable per tenant, so in Spanish and Hebrew the
 * words AROUND them cannot be written against the default word's gender. A tenant who
 * renames Company ("Empresa", feminine) to "Cliente" (masculine) would otherwise read
 * "una Cliente". So the terminology provider publishes a gender variable beside each
 * term's four word variables — `{{companyGender}}` → `'m' | 'f' | 'n'` — and a gendered
 * string selects on it:
 *
 *     "Define un patrón para {{companyGender, agree(m: un; f: una)}} {{company}}"
 *     "השאר {{company}} {{companyGender, agree(m: ריק; f: ריקה)}}"
 *
 * Both halves come from the term config (`shared/config/i18n_terms.config.json`
 * `defaults.<locale>.gender`) or, when the tenant declared one, from their override row
 * — so the article and the noun always move together.
 *
 * The backend translator implements the identical selection
 * (`shared/i18n/translator.py::_select_agreement`) so one string renders the same in the
 * app and in a server-rendered email. `tools/check_i18n_gendered_term_agreement.py`
 * gates both the adjacency this exists to fix and the requirement that every `agree()`
 * declares both `m` and `f`.
 *
 * Contract: docs/platform/i18n-terminology-overrides-design.md § Grammatical gender.
 */
import type { i18n as I18nInstance } from 'i18next';

/** The branch keys i18next hands us alongside its own `t()` options. */
const GENDER_KEYS = ['m', 'f', 'n'] as const;

type GenderKey = (typeof GENDER_KEYS)[number];

/**
 * i18next parses `agree(m: un; f: una)` into `{ m: 'un', f: 'una' }` and merges it into
 * the options object, so read only the keys this formatter owns — anything else in there
 * belongs to the `t()` call.
 */
function branches(options: Record<string, unknown> | undefined): Partial<Record<GenderKey, string>> {
  const found: Partial<Record<GenderKey, string>> = {};
  if (!options) return found;
  for (const key of GENDER_KEYS) {
    const value = options[key];
    if (value !== undefined && value !== null) found[key] = String(value);
  }
  return found;
}

export function selectAgreement(
  gender: unknown,
  options: Record<string, unknown> | undefined,
): string {
  const declared = branches(options);
  const key = String(gender) as GenderKey;
  if (declared[key] !== undefined) return declared[key] as string;
  // Fall through the FIXED order m, f, n — the same order, spelled the same way, as
  // `_select_agreement` in shared/i18n/translator.py. Fixed rather than "first declared"
  // because i18next hands us a merged options object with no declaration order to
  // recover, and the two implementations must not diverge even on the unreachable tail.
  // `m` leads as the unmarked form; the checker requires every agree() to declare it.
  for (const fallback of GENDER_KEYS) {
    if (declared[fallback] !== undefined) return declared[fallback] as string;
  }
  return '';
}

export function registerAgreeFormatter(i18n: I18nInstance): void {
  i18n.services.formatter?.add('agree', (value, _lng, options) =>
    selectAgreement(value, options as Record<string, unknown> | undefined),
  );
}
